import { Head, Link } from '@inertiajs/react';
import { type ReactElement } from 'react';

import {
    DesignFooter,
    DesignHeader,
    I,
    UtilityBar,
} from '@/Components/Design';

type Tile = { slug: string; name: string; icon: string | null; lawyersCount: number };
type Props = { practiceAreas: Tile[] };

const PA_ICON: Record<string, ReactElement> = {
    'car-accidents': I.car,
    'truck-accidents': I.truck,
    'motorcycle-accidents': I.bike,
    'slip-and-fall': I.fall,
    'medical-malpractice': I.med,
    'workers-compensation': I.work,
    'wrongful-death': I.death,
    'product-liability': I.product,
    'dog-bites': I.dog,
    'nursing-home-abuse': I.shield,
    'construction-accidents': I.work,
    'brain-and-spinal-injuries': I.med,
};

export default function PracticeAreasIndex({ practiceAreas }: Props) {
    return (
        <>
            <Head>
                <title>Practice areas — Personal injury directory</title>
            </Head>
            <div style={{ background: 'var(--paper)', minHeight: '100vh' }}>
                <UtilityBar />
                <DesignHeader active="Practice Areas" />

                <section data-pa-idx-section="hero" style={{ maxWidth: 1200, margin: '0 auto', padding: '64px 56px 40px' }}>
                    <nav aria-label="Breadcrumb">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-mute)', marginBottom: 24 }}>
                        <Link href="/" style={{ color: 'inherit', textDecoration: 'none' }}>Home</Link>
                        <span>/</span>
                        <span aria-current="page" style={{ color: 'var(--ink)' }}>Practice areas</span>
                    </div></nav>
                    <div style={{ fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ember)', marginBottom: 10, fontWeight: 600 }}>Browse by case type</div>
                    <h1 style={{ fontSize: 64, lineHeight: 1.05, letterSpacing: '-0.025em', marginBottom: 16, fontFamily: 'var(--font-serif)', fontWeight: 500 }}>
                        Practice areas
                    </h1>
                    <p style={{ fontSize: 18, color: 'var(--ink-soft)', lineHeight: 1.55, maxWidth: 620 }}>
                        Every case type we cover. Click through to see attorneys actively taking these cases.
                    </p>
                </section>

                <section data-pa-idx-section="list" style={{ maxWidth: 1200, margin: '0 auto', padding: '0 56px 80px' }}>
                    <div data-pa-idx-grid style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                        {practiceAreas.map((p) => (
                            <Link
                                key={p.slug}
                                href={`/${p.slug}`}
                                style={{
                                    background: 'var(--card)',
                                    border: '1px solid var(--rule)',
                                    borderRadius: 14,
                                    padding: 24,
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 12,
                                    transition: 'all var(--dur) var(--ease)',
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div
                                        style={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: 10,
                                            background: 'var(--paper-deep)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'var(--ink)',
                                        }}
                                    >
                                        {PA_ICON[p.slug] ?? I.scales}
                                    </div>
                                    {p.lawyersCount > 0 && (
                                        <div className="pip-mono" style={{ fontSize: 12, color: 'var(--ember)', fontWeight: 600 }}>
                                            {p.lawyersCount.toLocaleString()}
                                        </div>
                                    )}
                                </div>
                                <div style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 500, lineHeight: 1.2 }}>
                                    {p.name}
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>

                <DesignFooter />
            </div>
        </>
    );
}
